import * as React from "react";
import { Box, FormHelperText, useMediaQuery, useTheme } from "@mui/material";
import { between } from "../style/style";
/** Matches the input border token used by the textfield theme override. */
const OTP_BORDER = "#919EAB33";
/**
 * Opaque fill. Kept flat rather than the translucent #919EAB14 used by other
 * inputs because this card is frosted over a photo, where an alpha fill samples
 * a different backdrop under each box and the circles no longer match.
 */
const OTP_FILL = "#dae1e3";
export const OTPInput = React.forwardRef(({ length, value, onChange, error }, ref) => {
    const [otp, setOTP] = React.useState(value);
    const inputRefs = React.useRef(new Array(length).fill(null));
    const focusInput = (targetIndex) => {
        const targetInput = inputRefs.current[targetIndex];
        if (targetInput) {
            targetInput.focus();
        }
    };
    const selectInput = (targetIndex) => {
        const targetInput = inputRefs.current[targetIndex];
        if (targetInput) {
            targetInput.select();
        }
    };
    const handleKeyDown = (event, currentIndex) => {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowDown":
            case " ":
                event.preventDefault();
                break;
            case "ArrowLeft":
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                    selectInput(currentIndex - 1);
                }
                break;
            case "ArrowRight":
                event.preventDefault();
                if (currentIndex < length - 1) {
                    focusInput(currentIndex + 1);
                    selectInput(currentIndex + 1);
                }
                break;
            case "Delete":
                event.preventDefault();
                setOTP((prevOtp) => {
                    const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
                    return otp;
                });
                break;
            case "Backspace":
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                    selectInput(currentIndex - 1);
                }
                setOTP((prevOtp) => {
                    const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
                    return otp;
                });
                break;
            default:
                break;
        }
    };
    const handleChange = (event, currentIndex) => {
        var _a;
        const currentValue = event.target.value;
        let indexToEnter = 0;
        while (indexToEnter <= currentIndex) {
            if (((_a = inputRefs.current[indexToEnter]) === null || _a === void 0 ? void 0 : _a.value) &&
                indexToEnter < currentIndex) {
                indexToEnter += 1;
            }
            else {
                break;
            }
        }
        setOTP((prev) => {
            const otpArray = prev.split("");
            const lastValue = currentValue[currentValue.length - 1];
            otpArray[indexToEnter] = lastValue;
            return otpArray.join("");
        });
        if (currentValue !== "" && currentIndex < length - 1) {
            focusInput(currentIndex + 1);
        }
    };
    const handleClick = (event, currentIndex) => {
        selectInput(currentIndex);
    };
    const handlePaste = (event, currentIndex) => {
        var _a, _b;
        event.preventDefault();
        const clipboardData = event.clipboardData;
        if (clipboardData.types.includes("text/plain")) {
            let pastedText = clipboardData.getData("text/plain");
            pastedText = pastedText.substring(0, length).trim();
            let indexToEnter = 0;
            while (indexToEnter <= currentIndex) {
                if (((_a = inputRefs.current[indexToEnter]) === null || _a === void 0 ? void 0 : _a.value) &&
                    indexToEnter < currentIndex) {
                    indexToEnter += 1;
                }
                else {
                    break;
                }
            }
            const otpArray = value.split("");
            for (let i = indexToEnter; i < length; i += 1) {
                const lastValue = (_b = pastedText[i - indexToEnter]) !== null && _b !== void 0 ? _b : " ";
                otpArray[i] = lastValue;
            }
            setOTP(otpArray.join(""));
        }
    };
    React.useEffect(() => {
        onChange(otp);
    }, [otp, onChange]);
    React.useEffect(() => {
        if (ref && typeof ref === "function") {
            ref(inputRefs.current[0]);
        }
        else if (ref && "current" in ref) {
            ref.current = inputRefs.current[0];
        }
    }, [ref]);
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down("sm"));
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: Object.assign(Object.assign({}, between), { gap: 0.5 }) }, new Array(length).fill(null).map((_, index) => {
            var _a;
            return (React.createElement(React.Fragment, { key: index },
                React.createElement("input", { type: "number", "aria-label": `Digit ${index + 1} of OTP`, ref: (ele) => {
                        inputRefs.current[index] = ele;
                    }, onKeyDown: (event) => handleKeyDown(event, index), onChange: (event) => handleChange(event, index), onClick: (event) => handleClick(event, index), onPaste: (event) => handlePaste(event, index), value: (_a = value[index]) !== null && _a !== void 0 ? _a : "", style: {
                        width: isSm ? '37px' : "55px",
                        height: isSm ? '53px' : "53px",
                        fontSize: "0.875rem",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        padding: "8px 0",
                        borderRadius: "40px",
                        textAlign: "center",
                        color: theme.palette.text.primary,
                        backgroundColor: OTP_FILL,
                        border: `1px solid ${OTP_BORDER}`,
                        outline: "none",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    }, onFocus: (e) => {
                        e.target.style.borderColor = theme.palette.primary.main;
                        e.target.style.boxShadow = `0 0 0 2px ${theme.palette.primary.main}29`;
                    }, onBlur: (e) => {
                        e.target.style.borderColor = OTP_BORDER;
                        e.target.style.boxShadow = "none";
                    } })));
        })),
        React.createElement(FormHelperText, { error: true }, error)));
});
